// Signup source (SIGNUP_FUNNEL_REPORT Part A). The website passes its first touch on every
// sign-up / sign-in link as `ref`, `landing` and `utm_*` (site/src/lib/attribution.ts); this
// file keeps the first one the app sees until the tenant insert writes it to
// `tenants.signup_attribution`, which the browser can write once and never update.

export const ATTRIBUTION_KEY = 'signupAttribution';
const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'] as const;
const MAX_VALUE = 200;
// The localStorage copy survives a verification link opened later; a visit older than this
// is not this signup's source.
export const ATTRIBUTION_MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;
// Not /auth/callback: a verification link opened in another browser would record the email
// client as a fresh first touch.
const CAPTURE_PATHS = new Set(['/auth/signup', '/auth/signin']);

export interface SignupAttribution {
  heard_from?: string;
  site_referrer?: string;
  landing_path?: string;
  utm?: Record<string, string>;
  app_referrer?: string;
  captured_at?: string;
}

const clip = (v: string) => v.slice(0, MAX_VALUE);

function readStored(now: number): SignupAttribution | null {
  const parse = (raw: string | null): SignupAttribution | null => {
    if (!raw) return null;
    try {
      const v: unknown = JSON.parse(raw);
      return v && typeof v === 'object' && !Array.isArray(v) ? (v as SignupAttribution) : null;
    } catch {
      return null;
    }
  };
  try {
    const session = parse(sessionStorage.getItem(ATTRIBUTION_KEY));
    if (session) return session;
  } catch {
    // storage blocked
  }
  try {
    const backup = parse(localStorage.getItem(ATTRIBUTION_KEY));
    const at = backup?.captured_at ? Date.parse(backup.captured_at) : NaN;
    if (backup && Number.isFinite(at) && now - at <= ATTRIBUTION_MAX_AGE_MS) return backup;
  } catch {
    // storage blocked
  }
  return null;
}

/** Records the first touch, once, on the sign-up and sign-in pages. Call before render. */
export function captureSignupAttribution(
  location: { pathname: string; search: string } = window.location,
  referrer: string = document.referrer,
  now: number = Date.now(),
): void {
  if (!CAPTURE_PATHS.has(location.pathname)) return;
  if (readStored(now)) return;

  const params = new URLSearchParams(location.search);
  const touch: SignupAttribution = {};
  // `ref` present and empty means the site saw a direct visit; absent means no site hop.
  if (params.has('ref')) touch.site_referrer = clip(params.get('ref') ?? '');
  const landing = params.get('landing');
  if (landing) touch.landing_path = clip(landing);
  const utm: Record<string, string> = {};
  for (const key of UTM_KEYS) {
    const v = params.get(key);
    if (v) utm[key] = clip(v);
  }
  if (Object.keys(utm).length > 0) touch.utm = utm;
  touch.app_referrer = clip(referrer);
  // eslint-disable-next-line no-restricted-syntax -- `now` is epoch milliseconds, not a date-only string
  touch.captured_at = new Date(now).toISOString();

  const raw = JSON.stringify(touch);
  try {
    sessionStorage.setItem(ATTRIBUTION_KEY, raw);
  } catch {
    // storage blocked
  }
  try {
    localStorage.setItem(ATTRIBUTION_KEY, raw);
  } catch {
    // storage blocked
  }
}

/** The object the tenant insert writes, or null when nothing was captured or typed. */
export function buildSignupAttribution(
  heardFrom?: string | null,
  now: number = Date.now(),
): SignupAttribution | null {
  const stored = readStored(now) ?? {};
  const heard = heardFrom?.trim();
  const out: SignupAttribution = { ...stored, ...(heard ? { heard_from: clip(heard) } : {}) };
  return Object.keys(out).length > 0 ? out : null;
}

export function clearSignupAttribution(): void {
  try {
    sessionStorage.removeItem(ATTRIBUTION_KEY);
  } catch {
    // storage blocked
  }
  try {
    localStorage.removeItem(ATTRIBUTION_KEY);
  } catch {
    // storage blocked
  }
}
